import { useEffect, useMemo, useState } from "react";
import { Check, ChevronRight, Home, Menu, Plus, Send, Target, Trash2 } from "lucide-react";
import { clsx } from "clsx";
import {
  activeChildren,
  branchTasks,
  breadcrumbPath,
  daysLeftLabel,
  fractionProgress,
  isLeaf,
  remainingDescendants,
} from "../lib/goalCore";
import { todayIso } from "../lib/laneCore";
import { isTauriEnvironment, type Goal, type GoalStatus, type Task } from "../lib/tauri";
import { useGoalStore } from "../stores/goalStore";
import { useTaskStore } from "../stores/taskStore";
import { useUiStore } from "../stores/uiStore";
import { GlobalSearchButton } from "./GlobalSearch";

const STATUS_OPTIONS: Array<{ value: GoalStatus; label: string }> = [
  { value: "todo", label: "未着手" },
  { value: "in_progress", label: "進行中" },
  { value: "done", label: "完了" },
  { value: "dormant", label: "休眠" },
];

const TITLE_PLACEHOLDER = "タイトルを「〜する」の形で入力";

export function GoalsView({ onMenuClick }: { onMenuClick?: () => void }) {
  const goals = useGoalStore((s) => s.goals);
  const loaded = useGoalStore((s) => s.loaded);
  const focusId = useGoalStore((s) => s.focusId);
  const load = useGoalStore((s) => s.load);
  const setFocus = useGoalStore((s) => s.setFocus);
  const allTasks = useTaskStore((s) => s.allTasks);

  // 初回ロード + 外部（HTTP/将来のMCP）書き込みの即時反映（LaneBoard の kpi-changed と同型）
  useEffect(() => {
    void load();
    if (!isTauriEnvironment()) return;
    let unlisten: (() => void) | undefined;
    let disposed = false;
    void import("@tauri-apps/api/event").then(({ listen }) =>
      listen("flow://goals-changed", () => {
        void load();
      }).then((fn) => {
        if (disposed) fn();
        else unlisten = fn;
      })
    );
    return () => {
      disposed = true;
      unlisten?.();
    };
  }, [load]);

  const focus = useMemo(
    () => (focusId ? goals.find((g) => g.id === focusId && !g.deleted) ?? null : null),
    [goals, focusId]
  );
  const crumbs = useMemo(() => (focus ? breadcrumbPath(goals, focus.id) : []), [goals, focus]);
  const children = useMemo(() => activeChildren(goals, focus?.id ?? null), [goals, focus]);
  const today = todayIso();

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex items-center gap-3 border-b border-border px-4 py-3 dark:border-border-dark">
        <button
          type="button"
          aria-label="メニュー"
          onClick={onMenuClick}
          className="flex h-9 w-9 items-center justify-center rounded-md text-muted hover:bg-hover dark:text-muted-dark dark:hover:bg-hover-dark md:hidden"
        >
          <Menu className="h-5 w-5" />
        </button>
        <h2 className="flex items-center gap-2 text-base font-bold">
          <Target className="h-4 w-4" /> ゴール
        </h2>
        <GlobalSearchButton className="ml-auto" />
      </header>

      {/* 部品1: 1画面=1枝フォーカス + パンくず（巨大ツリー画面は作らない） */}
      <nav className="flex flex-wrap items-center gap-1 border-b border-border px-4 py-2 text-xs dark:border-border-dark">
        <button
          type="button"
          onClick={() => setFocus(null)}
          className="flex items-center gap-1 text-muted hover:text-text dark:text-muted-dark dark:hover:text-text-dark"
        >
          <Home className="h-3.5 w-3.5" /> ルート
        </button>
        {crumbs.map((c: Goal, i: number) => (
          <span key={c.id} className="flex items-center gap-1">
            <ChevronRight className="h-3 w-3 text-dim" />
            {i === crumbs.length - 1 ? (
              <span className="max-w-[200px] truncate font-semibold">{c.title}</span>
            ) : (
              <button
                type="button"
                onClick={() => setFocus(c.id)}
                className="max-w-[160px] truncate text-muted hover:text-text dark:text-muted-dark dark:hover:text-text-dark"
              >
                {c.title}
              </button>
            )}
          </span>
        ))}
      </nav>

      <div className="flow-scrollbar min-h-0 flex-1 overflow-y-auto px-4 py-3">
        {!isTauriEnvironment() && loaded && goals.length === 0 ? (
          <p className="py-12 text-center text-sm text-muted dark:text-muted-dark">
            ゴールはデスクトップ版で利用できます（iPhone 同期は G2 で対応予定）
          </p>
        ) : (
          <>
            {focus && <GoalEditor key={focus.id} goal={focus} />}
            <div className="mt-3 space-y-2">
              {children.map((child: Goal) => (
                <GoalCard key={child.id} goal={child} goals={goals} tasks={allTasks} today={today} />
              ))}
              {children.length === 0 && !focus && loaded && (
                <p className="py-8 text-center text-xs text-dim">
                  ルートゴールがありません。下の入力欄から追加してください
                </p>
              )}
            </div>
            {focus && isLeaf(goals, focus.id) && <BranchTasks goal={focus} />}
            <ChildAdd parentId={focus?.id ?? null} />
            {!focus && <DormantSection goals={goals} />}
          </>
        )}
      </div>
    </div>
  );
}

/** m-5b: ルート画面の折りたたみ「休眠中」セクション。dormant はアクティブツリーから消えるため、
 * ここが唯一の復帰導線（「復帰」ワンタップで todo に戻す）。バッジは付けない（赤バッジ洪水回避） */
function DormantSection({ goals }: { goals: Goal[] }) {
  const update = useGoalStore((s) => s.update);
  const setFocus = useGoalStore((s) => s.setFocus);
  const showToast = useUiStore((s) => s.showToast);
  const [open, setOpen] = useState(false);
  const dormant = useMemo(
    () => goals.filter((g) => !g.deleted && g.status === "dormant"),
    [goals]
  );

  if (dormant.length === 0) return null;

  const wake = async (goal: Goal) => {
    try {
      await update(goal.id, { status: "todo" });
      showToast(`「${goal.title}」を復帰しました`);
    } catch (e) {
      showToast(e instanceof Error ? e.message : "復帰に失敗しました");
    }
  };

  return (
    <div className="mt-4 border-t border-border pt-3 dark:border-border-dark">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 text-xs text-muted hover:text-text dark:text-muted-dark dark:hover:text-text-dark"
      >
        <ChevronRight className={clsx("h-3.5 w-3.5 transition-transform", open && "rotate-90")} />
        休眠中 ({dormant.length})
      </button>
      {open && (
        <div className="mt-2 space-y-1.5">
          {dormant.map((g) => {
            // ツリー上の位置がわかるよう親パスを添える（dormant 自身は activeChildren に出ないため breadcrumb で辿る）
            const path = breadcrumbPath(goals, g.id);
            const parentPath = path.slice(0, -1).map((p) => p.title).join(" › ");
            return (
              <div
                key={g.id}
                className="flex items-center gap-2 rounded-md border border-border bg-bg p-2 dark:border-border-dark dark:bg-bg-dark"
              >
                <div className="min-w-0 flex-1">
                  <button
                    type="button"
                    onClick={() => setFocus(g.id)}
                    className="block w-full truncate text-left text-sm text-muted dark:text-muted-dark"
                  >
                    {g.title}
                  </button>
                  {parentPath && (
                    <span className="block truncate text-[10px] text-dim">{parentPath}</span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => void wake(g)}
                  className="shrink-0 rounded-md border border-brand-500/40 bg-brand-500/10 px-2 py-1 text-[11px] font-semibold text-brand-700 dark:text-brand-200"
                >
                  復帰
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** 子ゴールのカード。タイトルタップで枝へ潜る（部品2/3/4 のバッジ・チップ付き） */
function GoalCard({
  goal,
  goals,
  tasks,
  today,
}: {
  goal: Goal;
  goals: Goal[];
  tasks: Task[];
  today: string;
}) {
  const setFocus = useGoalStore((s) => s.setFocus);
  const update = useGoalStore((s) => s.update);
  const showToast = useUiStore((s) => s.showToast);
  const weight = remainingDescendants(goals, goal.id);
  const fraction = fractionProgress(goals, tasks, goal.id);
  const due = daysLeftLabel(goal.dueDate, today);

  const setStatus = async (status: GoalStatus) => {
    try {
      await update(goal.id, { status });
    } catch (e) {
      showToast(e instanceof Error ? e.message : "更新に失敗しました");
    }
  };

  return (
    <div className="flex items-center gap-2 rounded-lg border border-border bg-card p-3 dark:border-border-dark dark:bg-card-dark">
      {/* 部品2: 枝の重さバッジ（残り子孫数。0 は出さない） */}
      {weight > 0 && (
        <span className="flex h-6 min-w-6 shrink-0 items-center justify-center rounded-full bg-brand-500/15 px-1.5 font-mono text-xs font-semibold text-brand-700 dark:text-brand-200">
          {weight}
        </span>
      )}
      <button type="button" onClick={() => setFocus(goal.id)} className="min-w-0 flex-1 text-left">
        <span
          className={clsx(
            "block truncate text-sm font-medium",
            goal.status === "done" && "text-muted line-through dark:text-muted-dark"
          )}
        >
          {goal.title}
        </span>
      </button>
      {/* 部品3: 分数進捗（子がいる時のみ。0/0 は出さない） */}
      {fraction && (
        <span className="shrink-0 font-mono text-xs text-muted dark:text-muted-dark">
          {fraction.done}/{fraction.total}
        </span>
      )}
      {/* 部品4: あと◯日チップ（超過は赤） */}
      {due && (
        <span
          className={clsx(
            "shrink-0 rounded-full px-2 py-0.5 text-[10px]",
            due.overdue
              ? "bg-danger-500/10 font-semibold text-danger-500"
              : "bg-bg font-medium text-muted dark:bg-bg-dark dark:text-muted-dark"
          )}
        >
          {due.text}
        </span>
      )}
      <select
        aria-label="ステータス"
        value={goal.status}
        onChange={(e) => void setStatus(e.target.value as GoalStatus)}
        className="shrink-0 rounded border border-border bg-bg px-1 py-0.5 text-[10px] text-muted dark:border-border-dark dark:bg-bg-dark dark:text-muted-dark"
      >
        {STATUS_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

/** 部品5: モーダルなしインライン編集（blur / Enter 確定。IME 変換 Enter では確定しない） */
function InlineField({
  label,
  value,
  placeholder,
  multiline = false,
  onCommit,
}: {
  label: string;
  value: string;
  placeholder: string;
  multiline?: boolean;
  onCommit: (next: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  const commit = () => {
    if (draft.trim() !== value.trim()) onCommit(draft.trim());
  };
  const common =
    "w-full rounded-md border border-transparent bg-transparent px-2 py-1 text-sm hover:border-border focus:border-brand-500 focus:bg-bg focus:outline-none dark:hover:border-border-dark dark:focus:bg-bg-dark";
  return (
    <label className="block">
      <span className="mb-0.5 block text-[10px] font-semibold text-dim">{label}</span>
      {multiline ? (
        <textarea
          value={draft}
          rows={2}
          placeholder={placeholder}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          className={common}
        />
      ) : (
        <input
          value={draft}
          placeholder={placeholder}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            // IME 変換確定の Enter で送信しない（InboxView と同じ防御）
            if (e.key === "Enter" && !e.nativeEvent.isComposing) e.currentTarget.blur();
          }}
          className={common}
        />
      )}
    </label>
  );
}

/** フォーカス中の枝の詳細（タイトル・現状・DoD・期日すべてインライン編集） */
function GoalEditor({ goal }: { goal: Goal }) {
  const update = useGoalStore((s) => s.update);
  const remove = useGoalStore((s) => s.remove);
  const setFocus = useGoalStore((s) => s.setFocus);
  const showToast = useUiStore((s) => s.showToast);

  const patch = async (p: Parameters<typeof update>[1]) => {
    try {
      await update(goal.id, p);
    } catch (e) {
      showToast(e instanceof Error ? e.message : "更新に失敗しました");
    }
  };

  const removeGoal = async () => {
    try {
      await remove(goal.id);
      setFocus(goal.parentId);
      showToast("ゴールを削除しました");
    } catch (e) {
      // 子持ち削除拒否などはバックエンド文言をそのまま見せる
      showToast(e instanceof Error ? e.message : "削除に失敗しました");
    }
  };

  return (
    <div className="space-y-2 rounded-lg border border-border bg-card p-3 dark:border-border-dark dark:bg-card-dark">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <InlineField
            label="ゴール"
            value={goal.title}
            placeholder={TITLE_PLACEHOLDER}
            onCommit={(v) => {
              if (v) void patch({ title: v });
            }}
          />
        </div>
        <button
          type="button"
          aria-label="ゴールを削除"
          onClick={() => void removeGoal()}
          className="mt-4 rounded p-1.5 text-muted hover:text-danger-500 dark:text-muted-dark"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
      <InlineField
        label="現状"
        value={goal.description}
        placeholder="いまの状態を書く"
        multiline
        onCommit={(v) => void patch({ description: v })}
      />
      <InlineField
        label="完了の基準（DoD）"
        value={goal.dod}
        placeholder="何が観測できたら完了か"
        multiline
        onCommit={(v) => void patch({ dod: v })}
      />
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-semibold text-dim">期日</span>
        <input
          type="date"
          value={goal.dueDate ?? ""}
          onChange={(e) =>
            void patch(e.target.value ? { dueDate: e.target.value } : { clearDueDate: true })
          }
          className="rounded border border-border bg-bg px-2 py-1 text-xs dark:border-border-dark dark:bg-bg-dark"
        />
      </div>
    </div>
  );
}

/** 子の追加は最下部入力欄（設計書 部品5。「〜する」形プレースホルダ + IME対応） */
function ChildAdd({ parentId }: { parentId: string | null }) {
  const create = useGoalStore((s) => s.create);
  const showToast = useUiStore((s) => s.showToast);
  const [title, setTitle] = useState("");

  const submit = async () => {
    if (!title.trim()) return;
    try {
      await create({ title: title.trim(), parentId });
      setTitle("");
    } catch (e) {
      showToast(e instanceof Error ? e.message : "ゴールの追加に失敗しました");
    }
  };

  return (
    <div className="mt-3 flex items-center gap-2">
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.nativeEvent.isComposing) void submit();
        }}
        placeholder={TITLE_PLACEHOLDER}
        className="h-10 flex-1 rounded-md border border-border bg-bg px-3 text-sm dark:border-border-dark dark:bg-bg-dark"
      />
      <button
        type="button"
        aria-label="ゴールを追加"
        onClick={() => void submit()}
        disabled={!title.trim()}
        className="flex h-10 w-10 items-center justify-center rounded-md bg-brand-500 text-white disabled:opacity-50"
      >
        <Plus className="h-5 w-5" />
      </button>
    </div>
  );
}

/** 葉ゴールの「この枝のタスク」。構想→実行を同一画面で繋ぐ（設計書: 今日へ送るワンタップ） */
function BranchTasks({ goal }: { goal: Goal }) {
  const allTasks = useTaskStore((s) => s.allTasks);
  const createTask = useTaskStore((s) => s.createTask);
  const updateTask = useTaskStore((s) => s.updateTask);
  const toggleTask = useTaskStore((s) => s.toggleTask);
  const showToast = useUiStore((s) => s.showToast);
  const [title, setTitle] = useState("");
  const today = todayIso();
  const tasks = useMemo(() => branchTasks(allTasks, goal.id), [allTasks, goal.id]);

  const add = async () => {
    if (!title.trim()) return;
    try {
      // goal_id 付きで直接作る（インボックス経由しない — isInboxTask が goalId を除外）
      await createTask({ title: title.trim(), goalId: goal.id });
      setTitle("");
    } catch (e) {
      showToast(e instanceof Error ? e.message : "タスクの追加に失敗しました");
    }
  };

  const sendToToday = async (taskId: string) => {
    try {
      // dueDate=今日（ローカル）で今日レーンに出る（laneCore sectionFor の "today" 判定と同じ基準）
      await updateTask(taskId, { dueDate: today });
      showToast("今日レーンに送りました");
    } catch (e) {
      showToast(e instanceof Error ? e.message : "更新に失敗しました");
    }
  };

  return (
    <div className="mt-3 rounded-lg border border-border bg-card p-3 dark:border-border-dark dark:bg-card-dark">
      <div className="mb-2 text-[10px] font-semibold text-dim">この枝のタスク ({tasks.length})</div>
      <div className="space-y-1.5">
        {tasks.map((task: Task) => {
          const due = daysLeftLabel(task.dueDate, today);
          const isTodayAlready = Boolean(task.dueDate && task.dueDate <= today);
          return (
            <div
              key={task.id}
              className="flex items-center gap-2 rounded-md border border-border bg-bg p-2 dark:border-border-dark dark:bg-bg-dark"
            >
              <button
                type="button"
                aria-label="完了にする"
                onClick={() => void toggleTask(task.id)}
                className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-border text-transparent hover:border-brand-500 hover:text-brand-500 dark:border-border-dark"
              >
                <Check className="h-3 w-3" />
              </button>
              <span className="min-w-0 flex-1 truncate text-sm">{task.title}</span>
              {due && (
                <span
                  className={clsx(
                    "shrink-0 text-[10px]",
                    due.overdue ? "font-semibold text-danger-500" : "text-muted dark:text-muted-dark"
                  )}
                >
                  {due.text}
                </span>
              )}
              {!isTodayAlready && (
                <button
                  type="button"
                  onClick={() => void sendToToday(task.id)}
                  className="flex shrink-0 items-center gap-1 rounded-md border border-brand-500/40 bg-brand-500/10 px-2 py-1 text-[11px] font-semibold text-brand-700 dark:text-brand-200"
                >
                  <Send className="h-3 w-3" /> 今日へ送る
                </button>
              )}
            </div>
          );
        })}
        {tasks.length === 0 && (
          <p className="py-3 text-center text-xs text-dim">
            タスクなし。下の入力欄からこの枝に直結で追加できます
          </p>
        )}
      </div>
      <div className="mt-2 flex items-center gap-2">
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.nativeEvent.isComposing) void add();
          }}
          placeholder="この枝の実タスクを追加（Enter）"
          className="h-9 flex-1 rounded-md border border-border bg-bg px-2 text-sm dark:border-border-dark dark:bg-bg-dark"
        />
        <button
          type="button"
          aria-label="タスクを追加"
          onClick={() => void add()}
          disabled={!title.trim()}
          className="flex h-9 w-9 items-center justify-center rounded-md bg-brand-500 text-white disabled:opacity-50"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
